import React, { useState, useEffect, useRef } from 'react';
import { Users, Calendar, Store, ShoppingBag } from 'lucide-react';

const StatItem = ({ icon: Icon, endValue, suffix, label, delay }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          // Start animation after delay
          setTimeout(() => {
            const duration = 2000;
            const steps = 60;
            const increment = endValue / steps;
            let current = 0;
            
            const timer = setInterval(() => {
              current += increment;
              if (current >= endValue) {
                setCount(endValue);
                clearInterval(timer);
              } else {
                setCount(Math.floor(current));
              }
            }, duration / steps);
          }, delay);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [endValue, delay, hasAnimated]);

  return (
    <div ref={ref} className="text-center p-6">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF4D00]/10 mb-4">
        <Icon className="w-8 h-8 text-[#FF4D00]" />
      </div>
      <div className="text-4xl md:text-5xl font-bold text-neutral-900 mb-2">
        {count.toLocaleString()}{suffix}
      </div>
      <p className="text-neutral-600 font-medium">{label}</p>
    </div>
  );
};

const StatsCounter = () => {
  const stats = [
    { icon: Users, endValue: 10, suffix: 'K+', label: 'Happy Customers', delay: 0 },
    { icon: Calendar, endValue: 5, suffix: '+', label: 'Years of Excellence', delay: 200 },
    { icon: Store, endValue: 100, suffix: '+', label: 'Distributors', delay: 400 },
    { icon: ShoppingBag, endValue: 5000, suffix: '+', label: 'Retailers Nationwide', delay: 600 }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Trusted by <span className="text-[#FF4D00]">Thousands</span> Across India
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            From manufacturing excellence to nationwide distribution, RSD Accessories continues to grow with your trust.
          </p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {stats.map((stat, index) => (
            <StatItem key={index} {...stat} />
          ))}
        </div>
        
        {/* Trust Line */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-4 bg-white px-6 py-3 rounded-full shadow-lg">
            <span className="text-2xl">🇮🇳</span>
            <span className="font-semibold text-neutral-800">Proudly Made in India</span>
            <span className="text-[#FF4D00]">•</span>
            <span className="text-neutral-600">Premium Quality</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;
